import { cn } from "@repo/shared/utils";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { ConfirmDialog } from "@repo/ui/components/Base/ConfirmDialog";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import {
  ArrowDownIcon,
  ChevronDownIcon,
  FileIcon,
  FilePenLine,
  GripVerticalIcon,
  PencilIcon,
  PlusIcon,
  SectionIcon,
  SquareCheckBigIcon,
  XIcon,
} from "lucide-react";
import { forwardRef, memo, useState } from "react";
import type { EditorType } from "./utilities";

const iconButtonWrapperClasses =
  /*@tw*/ "flex-shrink-0 flex flex-row justify-center items-center w-8 h-8 left-1 relative active:top-[1px]";
const iconButtonClasses =
  /*@tw*/ "w-5 cursor-pointer opacity-0 group-hover:opacity-50 group-focus-within:opacity-50 relative -top-[1px]";
const superscriptIconClasses = /*@tw*/ "absolute w-4 h-4 mt-1 -right-2";

interface DragGripProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  icon?: React.ReactNode;
  titleNote?: string;
}

/** Drag-grip icon for items that can be dragged.
 *
 * When disabled, the icon is invisible, but the component still creates a space of the same
 * size. */
export const DragGrip = memo(
  forwardRef<HTMLDivElement, DragGripProps>(
    ({ disabled, icon, titleNote, ...props }: DragGripProps, ref) =>
      disabled ? (
        <div className="flex-shrink-0 w-8 h-8 flex flex-row justify-center items-center">
          {icon ? (
            <Tooltip
              tooltipTriggerProps={{ className: "inline-block opacity-50" }}
              label={titleNote}
            >
              {icon}
            </Tooltip>
          ) : null}
        </div>
      ) : (
        <Tooltip
          asChild
          label={
            <>
              Click and drag to reorder
              {titleNote ? (
                <>
                  <br />({titleNote})
                </>
              ) : null}
            </>
          }
        >
          <div
            ref={ref}
            className="flex-shrink-0 w-8 h-8 flex flex-row justify-center items-center rounded-full cursor-pointer hover:bg-black/5"
            {...props}
          >
            <div className="inline-block group-hover:hidden opacity-50">
              {icon}
            </div>
            <GripVerticalIcon
              className={cn(
                "w-6 text-slate-300",
                icon ? "hidden group-hover:inline-block" : "inline-block"
              )}
            />
          </div>
        </Tooltip>
      )
  )
);

/** 'Edit' pencil icon button. */
export const EditPencil = memo(
  ({
    onClick,
    className,
    wrapperClassName,
    title,
  }: {
    onClick?: React.MouseEventHandler;
    className?: string;
    wrapperClassName?: string;
    title?: string;
  }) => (
    <Tooltip asChild label={title}>
      <button
        type="button"
        className={cn(iconButtonWrapperClasses, wrapperClassName)}
        onClick={onClick}
      >
        <PencilIcon className={cn(iconButtonClasses, className)} />
      </button>
    </Tooltip>
  )
);

/** 'Sign document' icon button. */
export const SignDocument = memo(
  ({
    onClick,
    className,
    wrapperClassName,
    title,
  }: {
    onClick?: React.MouseEventHandler;
    className?: string;
    wrapperClassName?: string;
    title?: string;
  }) => (
    <Tooltip asChild label={title}>
      <button
        type="button"
        className={cn(iconButtonWrapperClasses, wrapperClassName)}
        onClick={onClick}
      >
        <FilePenLine className={cn(iconButtonClasses, className)} />
      </button>
    </Tooltip>
  )
);

/** 'Remove / Delete' X icon button.
 *
 * Includes the confirmation popup logic. `onClick` doesn't trigger until the user confirms the
 * popup.
 *
 * If `allowDelete` is true, the popup will also offer a checkbox to allow deleting the object
 * completely, instead of just removing it from the agenda. */
export const RemoveX = memo(
  ({
    onClick,
    className,
    type,
    label,
    title,
    allowDelete = false,
  }: {
    onClick?: (deleteChecked: boolean) => void | Promise<void>;
    className?: string;
    type: string;
    label: string;
    title: string;
    allowDelete?: boolean;
  }) => {
    const [open, setOpen] = useState(false);
    const [deleteChecked, setDeleteChecked] = useState(false);

    const handleOpenChange = (open: boolean) => {
      setOpen(open);
      if (!open) setDeleteChecked(false);
    };

    return (
      <>
        <Tooltip asChild label={title}>
          <button
            type="button"
            className={iconButtonWrapperClasses}
            onClick={() => handleOpenChange(true)}
          >
            <XIcon className={cn(iconButtonClasses, className)} />
          </button>
        </Tooltip>
        <ConfirmDialog
          open={open}
          setOpen={handleOpenChange}
          confirmLabel={
            deleteChecked ? "Permanently Delete" : "Remove from Agenda"
          }
          className="max-w-lg"
          message={
            <div>
              <p>
                Are you sure you want to remove this {type}, "{label}," from the
                agenda?
                {!allowDelete &&
                  " (Note, this will not delete it from the system.)"}
              </p>
              {allowDelete && (
                <CheckboxInput
                  className="mt-2"
                  orientation="horizontal"
                  checked={deleteChecked}
                  onCheckedChange={(checked) => setDeleteChecked(!!checked)}
                  label={
                    <>
                      Also delete this {type} from the system.
                      <br />
                      This action cannot be undone.
                    </>
                  }
                />
              )}
            </div>
          }
          onSubmit={async () => onClick?.(deleteChecked)}
        />
      </>
    );
  }
);

/** 'Collapse section' chevron icon button. */
export const CollapseChevron = memo(
  ({
    onClick,
    collapsed,
    className,
    title,
  }: {
    onClick?: React.MouseEventHandler;
    collapsed?: boolean;
    className?: string;
    title?: string;
  }) => (
    <Tooltip asChild label={(collapsed ? "Expand " : "Collapse ") + title}>
      <button
        type="button"
        className={cn(iconButtonWrapperClasses)}
        onClick={onClick}
      >
        <ChevronDownIcon
          className={cn(
            iconButtonClasses,
            collapsed && "rotate-180 opacity-100",
            className
          )}
        />
      </button>
    </Tooltip>
  )
);

/** Dynamic icon. Renders an icon dependent on the type of object: Agenda Section, Agenda Item,
 * or Document. */
export const TypeIcon = ({ type }: { type: EditorType }) =>
  type === "agendaModule" ? (
    <SectionIcon className={iconButtonClasses} />
  ) : type === "agendaItem" ? (
    <SquareCheckBigIcon className={iconButtonClasses} />
  ) : type === "document" ? (
    <FileIcon className={iconButtonClasses} />
  ) : null;

/** 'Add new' icon button. Shows the object type icon with a plus. */
export const AddNew = memo(
  ({
    onClick,
    title,
    className,
    type,
  }: {
    onClick?: React.MouseEventHandler;
    title?: string;
    className?: string;
    type: EditorType;
  }) => (
    <Tooltip asChild label={title}>
      <button
        type="button"
        className={cn(iconButtonWrapperClasses, "mr-2", className)}
        onClick={onClick}
      >
        <TypeIcon type={type} />
        <PlusIcon className={cn(iconButtonClasses, superscriptIconClasses)} />
      </button>
    </Tooltip>
  )
);

/** 'Load' icon button. Shows the object type icon with a down arrow. */
export const PullIn = memo(
  ({
    onClick,
    title,
    className,
    type,
  }: {
    onClick?: React.MouseEventHandler;
    title?: string;
    className?: string;
    type: EditorType;
  }) => (
    <Tooltip asChild label={title}>
      <button
        type="button"
        className={cn(iconButtonWrapperClasses, "mr-2", className)}
        onClick={onClick}
      >
        <TypeIcon type={type} />
        <ArrowDownIcon
          className={cn(iconButtonClasses, superscriptIconClasses)}
        />
      </button>
    </Tooltip>
  )
);

/** Tool icon button.
 *
 * Rendered as a white button with a shadow to give a floating effect. */
export const ToolButton = ({
  title,
  children,
  className,
  onClick,
}: {
  title?: string;
  children?: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler;
}) => (
  <Tooltip asChild label={title}>
    <button
      type="button"
      className={cn(
        "w-10 h-10 mb-2 flex items-center justify-center rounded-[400px] bg-white shadow-md border border-gray-100 relative active:top-[1px]",
        className
      )}
      onClick={onClick}
    >
      {children}
    </button>
  </Tooltip>
);
